'use client';

import Link from 'next/link';
import { useState, useEffect } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { PlayIcon } from '@hugeicons/core-free-icons';
import NextImage from '@/features/Image';
import axios from '@/lib/axios';

interface ShortVideo {
  id: string;
  slug: string;
  banner: string;
  title: string;
  views?: number;
  orientation: string;
  is_popular: number;
  created_at: string;
}

interface Filter {
  label: string;
  params: {
    order_by?: string;
    is_popular?: string;
  };
}

export default function ZepidtubeShorts({
  initialShorts = [],
}: {
  initialShorts?: ShortVideo[];
}) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [shortsData, setShortsData] = useState<ShortVideo[]>(initialShorts);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filters: Filter[] = [
    { label: 'Latest', params: { order_by: 'latest' } },
    { label: 'Popular', params: { is_popular: '1' } },
    { label: 'Oldest', params: { order_by: 'oldest' } },
  ];

  const fetchShorts = async (params: Record<string, string>) => {
    try {
      setLoading(true);
      setError(null);
      const queryString = new URLSearchParams(params).toString();
      const response = await axios.get(
        `/api/v2/youtube-videos?orientation=shorts&${queryString}`
      );
      setShortsData(response.data.data || []);
    } catch (err) {
      setError('Failed to fetch shorts');
      console.error('Error fetching shorts:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterShorts = (index: number) => {
    setActiveIndex(index);
    const selectedFilter = filters[index];
    fetchShorts(selectedFilter.params);
  };

  // Load initial data if not provided
  useEffect(() => {
    if (initialShorts.length === 0) {
      fetchShorts({ order_by: 'latest' });
    }
  }, []);

  return (
    <div className="shorts padding-top padding-bottom">
      <div className="container">
        <div className="section-header mb-4">
          <h5 className="pt-0 pb-0 mb-0">Shorts</h5>
          <div className="section-header__actions">
            {filters.map((filter, index) => (
              <button
                key={index}
                onClick={() => filterShorts(index)}
                disabled={loading}
                className={`btn btn-sm text-dark btn-outline ml-2 ${activeIndex === index ? 'active' : ''}`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-4">Loading shorts...</div>
        ) : error ? (
          <div className="text-center py-4 text-danger">{error}</div>
        ) : shortsData?.length > 0 ? (
          <div className="row g-4 justify-content-center">
            {shortsData.map((short) => (
              <div className="col-6 col-lg-3" key={short.id}>
                <article className="post post--short">
                  <figure className="post__media post__video">
                    <Link href={`/zepid-tube/${short.slug}`}>
                      <NextImage
                        src={short?.banner}
                        alt={short?.title}
                        width={1280}
                        height={500}
                        className="fade-in fadeIn"
                        style={{
                          animationDuration: '0.3s',
                          animationDelay: '0s',
                        }}
                      />
                    </Link>
                    <Link
                      href={`/zepid-tube/${short.slug}`}
                      className="btn__video btn__iframe"
                    >
                      <HugeiconsIcon icon={PlayIcon} size={26} />
                    </Link>
                  </figure>

                  {/* Uncomment to show title and views if needed */}
                  {/* <div className="post__body">
                    <h2 className="post__title">
                      <Link
                        href={`/zepid-tube/${short.slug}`}
                        className="btn__video btn__iframe"
                      >
                        {short.title}
                      </Link>
                    </h2>
                    <div className="post__footer">
                      <span className="post__footer-info">
                        {short.views} Views
                      </span>
                    </div>
                  </div> */}
                </article>
              </div>
            ))}
          </div>
        ) : (
          <div className="row g-4 justify-content-center">
            <div className="col-md-6 col-lg-4">
              <h2 className="post__title">No shorts available</h2>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
